import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useSlotGroup,
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { clean, stagger, galleryPhotos } from '../utils/format';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import type { Amenity } from './AmenitiesList';

interface Photo {
  image?: string;
  alt_text?: string;
  title?: string;
}

const homePhotoDef: SlotGroupDefinition = {
  label: 'Your Home · Photo',
  description: 'Shown under the Your Home intro. Until set, a gallery photo tagged Apartment is shown.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'A gallery photo of the inside of a home here (kitchen, living room, bedroom, bath or private yard) that shows what comes with the home. Leave empty if the gallery has no interior or private-space photo.',
    context: 'Photo under the Your Home intro on the Amenities page, beside the in-home amenity list (card thumb, about 400 by 260). Must differ from the Community photo just below it.',
  },
};

const communityPhotoDef: SlotGroupDefinition = {
  label: 'Community · Photo',
  description: 'Shown under the Community intro. Until set, a gallery photo tagged Community is shown.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: "A gallery photo of this property's shared spaces or grounds that shows the community amenities at their best. Leave empty if the gallery has no shared-space or grounds photo.",
    context: 'Photo under the Community intro on the Amenities page, beside the community amenity list (card thumb, about 400 by 260). Must differ from the Your Home photo just above it.',
  },
};

/** Columns follow the count so a long list never becomes a wall and a short one never looks half empty. */
const cols = (n: number) => (n < 8 ? 1 : n <= 24 ? 2 : 3);

const alist = (c: number) =>
  `mt-[34px] grid grow content-start gap-y-0 border-t border-t-[--q-secondary] [&_div]:border-b [&_div]:border-b-[--q-border] [&_div]:px-0 [&_div]:font-heading max-[1199px]:mt-0 max-[767px]:mt-3 max-[767px]:grid-cols-[1fr] max-[767px]:gap-0 ${
    c === 3
      ? 'grid-cols-[repeat(3,1fr)] gap-x-8 [&_div]:py-3 [&_div]:text-[19px] [&_div]:leading-[25px]'
      : `${c === 1 ? 'grid-cols-[1fr]' : 'grid-cols-[1fr_1fr]'} gap-x-10 [&_div]:py-3.5 [&_div]:text-[22px] [&_div]:leading-7 max-[767px]:[&_div]:py-3 max-[767px]:[&_div]:text-[19px] max-[767px]:[&_div]:leading-[25px]`
  }`;

const photoBox = 'photo mt-2 h-[260px] min-h-0 max-[767px]:h-[220px]';
const emptyBox =
  'mt-2 flex h-[260px] items-center justify-center rounded-md border border-dashed border-[--q-border] bg-[--q-surface] text-sm text-muted-foreground max-[767px]:h-[220px]';

export function AmenityGroups() {
  const p = useRawProperty();
  const editing = useIsEditing();
  const homeSlot = useSlotGroup('amenitygroups.home_photo', homePhotoDef);
  const communitySlot = useSlotGroup('amenitygroups.community_photo', communityPhotoDef);
  const home = useCollectionBinding<Amenity>('amenitygroups.home', { collection: 'amenities', filter: { group: 'Apartment' } });
  const community = useCollectionBinding<Amenity>('amenitygroups.community', { collection: 'amenities', filter: { group: 'Community' } });

  const photos = galleryPhotos<Photo>(p.photo_gallery);
  const other = ((p.amenities as Amenity[]) || []).filter((a) => clean(a.title) && a.group !== 'Apartment' && a.group !== 'Community');
  const homeItems = home.filter((a) => clean(a.title));
  const communityItems = community.filter((a) => clean(a.title));

  /** A pick that differs from the schema default wins; otherwise the gallery photo tagged with the group. */
  const photoFor = (slot: { image?: string | null; alt?: string | null }, defaultIndex: number, tag: string) => {
    if (slot.image && slot.image !== photos[defaultIndex]?.image) return { src: slot.image, alt: slot.alt || '' };
    const g = photos.find((x) => x.title === tag);
    return g ? { src: g.image, alt: g.alt_text || '' } : null;
  };
  const homePhoto = photoFor(homeSlot, 0, 'Apartment');
  const communityPhoto = photoFor(communitySlot, 1, 'Community');

  const showHome = homeItems.length > 0 || editing;
  const showCommunity = communityItems.length > 0 || editing;
  if (!showHome && !showCommunity && !other.length) return null;

  return (
    <section className="section">
      <div className="flex flex-col gap-20 max-[767px]:gap-14">
        {showHome && (
          <section className="flex gap-16 max-[1199px]:flex-col max-[1199px]:gap-6" id="your-home">
            <div className="flex w-[400px] shrink-0 flex-col gap-4 max-[1199px]:w-auto" data-reveal>
              <RichTextContent path="amenitygroups.home_eyebrow" as="div" className="eyebrow" />
              <RichTextContent path="amenitygroups.home_title" as="h2" className="h3 [&_p]:m-0" />
              <RichTextContent path="amenitygroups.home_lead" as="p" className="m-0 text-[15px] leading-6 text-muted-foreground" />
              <EditableSlotGroup groupPath="amenitygroups.home_photo" definition={homePhotoDef} className={homePhoto ? photoBox : editing ? emptyBox : ''}>
                {homePhoto ? (
                  <VibesitesImage url={homePhoto.src} alt={homePhoto.alt} sizes="(min-width: 1200px) 400px, 100vw" />
                ) : (
                  editing && 'Your Home photo'
                )}
              </EditableSlotGroup>
            </div>
            <EditableCollectionBinding bindingPath="amenitygroups.home" label="Your Home Amenities" className={alist(cols(homeItems.length))} style={stagger(1)}>
              {homeItems.map((a, i) => (
                <div key={i}>{a.title}</div>
              ))}
            </EditableCollectionBinding>
          </section>
        )}

        {showCommunity && (
          <section className="flex gap-16 max-[1199px]:flex-col max-[1199px]:gap-6" id="community">
            <div className="flex w-[400px] shrink-0 flex-col gap-4 max-[1199px]:w-auto" data-reveal>
              <RichTextContent path="amenitygroups.community_eyebrow" as="div" className="eyebrow" />
              <RichTextContent path="amenitygroups.community_title" as="h2" className="h3" />
              <RichTextContent path="amenitygroups.community_lead" as="p" className="m-0 text-[15px] leading-6 text-muted-foreground" />
              <EditableSlotGroup
                groupPath="amenitygroups.community_photo"
                definition={communityPhotoDef}
                className={communityPhoto ? photoBox : editing ? emptyBox : ''}
              >
                {communityPhoto ? (
                  <VibesitesImage url={communityPhoto.src} alt={communityPhoto.alt} sizes="(min-width: 1200px) 400px, 100vw" />
                ) : (
                  editing && 'Community photo'
                )}
              </EditableSlotGroup>
            </div>
            <EditableCollectionBinding bindingPath="amenitygroups.community" label="Community Amenities" className={alist(cols(communityItems.length))}>
              {communityItems.map((a, i) => (
                <div key={i}>{a.title}</div>
              ))}
            </EditableCollectionBinding>
          </section>
        )}

        {other.length > 0 && (
          <section className="flex gap-16 max-[1199px]:flex-col max-[1199px]:gap-6">
            <div className="flex w-[400px] shrink-0 flex-col gap-4 max-[1199px]:w-auto" data-reveal>
              <div className="eyebrow">More</div>
              <h2 className="h3">Also included</h2>
            </div>
            <div className={alist(cols(other.length))}>
              {other.map((a, i) => (
                <div key={i}>{a.title}</div>
              ))}
            </div>
          </section>
        )}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenityGroups',
  displayName: 'Amenity Groups',
  tokens: {
    'amenitygroups.home_eyebrow': { label: 'Your Home · Eyebrow', type: 'richtext', defaultValue: '<p>Your home</p>' },
    'amenitygroups.home_title': {
      label: 'Your Home · Title',
      type: 'richtext',
      defaultValue: '<p>Inside every <span data-type="field-token" data-field="property_name"></span> home</p>',
    },
    'amenitygroups.home_lead': { label: 'Your Home · Lead', type: 'richtext', defaultValue: '<p>What comes with the home before you add a thing.</p>' },
    'amenitygroups.community_eyebrow': { label: 'Community · Eyebrow', type: 'richtext', defaultValue: '<p>Community</p>' },
    'amenitygroups.community_title': { label: 'Community · Title', type: 'richtext', defaultValue: '<p>Outside your door</p>' },
    'amenitygroups.community_lead': { label: 'Community · Lead', type: 'richtext', defaultValue: '<p>The shared spaces and services around the neighborhood.</p>' },
  },
  slotGroups: {
    'amenitygroups.home_photo': homePhotoDef,
    'amenitygroups.community_photo': communityPhotoDef,
  },
  collectionBindings: {
    'amenitygroups.home': {
      label: 'Your Home Amenities',
      collection: 'amenities',
      filter: { group: 'Apartment' },
    },
    'amenitygroups.community': {
      label: 'Community Amenities',
      collection: 'amenities',
      filter: { group: 'Community' },
    },
  },
};

(AmenityGroups as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenityGroups;
